/**
 * The controls shared by the paged lists (Inbox, Approvals): a labelled
 * select, the date filter (the Dashboard's picker plus "All time"), and the
 * pager. Kept dumb: the pages own the URL state.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DateRange } from '../../lib/date-range';
import { presetRange } from '../../lib/date-range';
import { DEFAULT_PRESET, pageSummary } from '../../lib/filters/list-params';
import { DateRangePicker } from '../../pages/dashboard/DateRangePicker';
import { cn } from '../../lib/cn';

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  /** The "no filter" choice, e.g. "All brands". */
  allLabel: string;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'h-7 rounded-sm border border-line bg-surface px-1.5 text-xs text-ink-900',
        value !== '' && 'border-accent-600 text-accent-700',
      )}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function DateFilter({
  range,
  onChange,
}: {
  /** null = all time. */
  range: DateRange | null;
  onChange: (next: DateRange | null) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <DateRangePicker value={range ?? presetRange(DEFAULT_PRESET)} onChange={onChange} />
      <button
        type="button"
        onClick={() => onChange(range ? null : presetRange(DEFAULT_PRESET))}
        aria-pressed={range === null}
        className={cn(
          'h-7 rounded-sm px-2 text-xs font-medium transition-colors duration-fast',
          range === null ? 'bg-ink-900 text-paper' : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
        )}
      >
        All time
      </button>
    </div>
  );
}

export function Pager({
  page,
  pageSize,
  total,
  onPage,
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (next: number) => void;
  className?: string;
}) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className={cn('flex items-center justify-between gap-2 text-xs text-ink-500', className)}>
      <span className="tabular">{pageSummary(page, pageSize, total)}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded-sm p-1 shadow-hairline hover:text-ink-900 disabled:opacity-40"
        >
          <ChevronLeft size={14} aria-hidden />
        </button>
        <span className="tabular px-1">
          {Math.min(page, last)} / {last}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={page >= last}
          onClick={() => onPage(page + 1)}
          className="rounded-sm p-1 shadow-hairline hover:text-ink-900 disabled:opacity-40"
        >
          <ChevronRight size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}
